import React, { useMemo } from "react";
import {
  Button,
  FormLabel,
  Grid as GridBase,
  Paper,
  Typography,
} from "@mui/material";
import { useFormik, useFormikContext } from "formik";
import * as Yup from "yup";
import groupBy from "lodash/groupBy";

import BulkSelectionLayoutBase from "@/features/campaigns/components/bulkSelection/BulkSelectionLayout";
import { FormHelperText } from "@mui/material";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import RpMuiSelectBase from "@/components/muiCustom/select/RpMuiSelect";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";

type Id = number | string;

type CampaignCreative = {
  id: Id;
  [key: string]: unknown;
};

type ExtendCampaignData = {
  creative_select_max_value?: number | string | null;
  campaign_creatives?: CampaignCreative[];
};

type BulkFormValues = {
  extend_creatives: Id[];
};

type LocationPackage = {
  id: Id;
};

type LocationSelectionValue = {
  location_name?: string;
  locationInfo?: {
    packages_data?: LocationPackage[];
  };
  extend_creatives?: Id[];
  [key: string]: unknown;
};

type LocationFormikValues = {
  selectedLoc?: Record<Id, LocationSelectionValue>;
};

type MissedCreativeDetail = {
  location_name: string;
  creative_name: string;
};

type ExtendCreativeBulkSelectionProps = {
  onToggleBulkSelection?: (_open?: boolean) => void;
  campaignData?: ExtendCampaignData;
  checkedLocations?: Id[];
  setLocationValues: (
    _updater: (_prevValues: LocationFormikValues) => LocationFormikValues,
    _shouldValidate?: boolean
  ) => void;
};

const BulkSelectionLayout = BulkSelectionLayoutBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

const Grid = GridBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

const RpMuiSelect = RpMuiSelectBase as React.ComponentType<
  Record<string, unknown>
>;

const createValidationSchema = (campaignData?: ExtendCampaignData) => {
  return Yup.object().shape({
    extend_creatives: Yup.array()
      .of(Yup.mixed<Id>())
      .test(
        "min-one-creative",
        "Please select at least one creative",
        function (value) {
          if (!value?.length) {
            return this.createError({
              path: "extend_creatives",
              message: "Please select at least one creative",
            });
          }
          return true;
        }
      )
      .test("max-creatives", function (value) {
        const { creative_select_max_value } = campaignData || {};

        // If max value is null, treat it as 1.
        const maxValue =
          creative_select_max_value === null
            ? 1
            : Number(creative_select_max_value);

        if (value?.length > maxValue) {
          return this.createError({
            path: "extend_creatives",
            message: `You can only select up to ${maxValue} creatives`,
          });
        }

        return true;
      }),
  });
};

// Helper function to display creative not available error message
const displayCreativeNotApplied = (
  missedCreativeDetails: MissedCreativeDetail[]
) => {
  const groupByName = groupBy(missedCreativeDetails, "location_name");

  return RpSweetAlert.fire({
    html: (
      <>
        <p className="text-center fw-b text-dark fs-20">
          Hey! Some of the selected creatives are not available for some of the
          selected locations.
        </p>
        <div
          className="responsive-table w-100"
          style={{ height: 400, overflowY: "auto", overflowX: "hidden" }}
        >
          <table
            className="table table-sm table-bordered table-hover text-left not-available-creative-list"
            style={{ tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                <th>Location</th>
                <th>Not Available Creatives</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(groupByName)?.map((location_name) => (
                <tr key={location_name}>
                  <td>{location_name}</td>
                  <td>
                    {groupByName[location_name]
                      ?.map((l) => l.creative_name)
                      ?.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
    confirmButtonText: "OK",
    showCancelButton: false,
  });
};

function ExtendCreativeBulkSelection({
  onToggleBulkSelection,
  campaignData,
  checkedLocations,
  setLocationValues,
}: ExtendCreativeBulkSelectionProps) {
  const { values: locationValues } = useFormikContext<LocationFormikValues>();

  const creativeOptions = useMemo(() => {
    return campaignData?.campaign_creatives?.map((creative) => ({
      label: getCreativePackageLabel(creative),
      value: creative.id,
    }));
  }, [campaignData?.campaign_creatives]);

  const handleSubmit = async (
    values: BulkFormValues,
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (_isSubmitting: boolean) => void;
      resetForm: (_nextState?: {
        values?: BulkFormValues;
        touched?: Record<string, unknown>;
      }) => void;
    }
  ) => {
    try {
      if (shouldBlockUnsafeSubmission(values)) {
        setSubmitting(false);
        return;
      }

      if (!checkedLocations?.length) {
        RpSweetAlert.fire({
          title: "No locations selected",
          text: "Please select at least one location",
          icon: "error",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        return;
      }

      const extend_creatives = values?.extend_creatives || [];
      const selectedLoc = locationValues?.selectedLoc || {};
      const missedCreativeDetails: MissedCreativeDetail[] = [];

      checkedLocations.forEach((location) => {
        const locationData = selectedLoc[location] || {};

        const availableCreativeIds =
          locationData?.locationInfo?.packages_data?.map((pkg) => pkg.id) || [];

        extend_creatives.forEach((creativeId) => {
          if (!availableCreativeIds.includes(creativeId)) {
            const creative = campaignData?.campaign_creatives?.find(
              (c) => String(c.id) === String(creativeId)
            );
            const creativeName =
              getCreativePackageLabel(creative) || `Creative ID: ${creativeId}`;

            missedCreativeDetails.push({
              location_name:
                locationData?.location_name || `Location ID: ${location}`,
              creative_name: creativeName,
            });
          }
        });
      });

      setLocationValues((prevValues) => {
        const currentSelectedLoc = prevValues.selectedLoc || {};
        const updatedSelectedLoc = { ...currentSelectedLoc };
        checkedLocations.forEach((location) => {
          const locationData = currentSelectedLoc[location] || {};

          // Get available creative IDs from location's packages_data
          const availableCreativeIds =
            locationData?.locationInfo?.packages_data?.map((pkg) => pkg.id) ||
            [];

          // Filter extend_creatives to only include creatives that exist in this location
          const filteredExtendCreatives = extend_creatives.filter(
            (creativeId) => availableCreativeIds.includes(creativeId)
          );

          updatedSelectedLoc[location] = {
            ...locationData,
            extend_creatives: filteredExtendCreatives,
          };
        });
        return {
          ...prevValues,
          selectedLoc: updatedSelectedLoc,
        };
      }, true);

      // Show error message if some creatives are not available
      if (missedCreativeDetails.length > 0) {
        displayCreativeNotApplied(missedCreativeDetails).then(() => {});
      } else {
        RpSweetAlert.fire({
          text: "Bulk selections successful!",
          icon: "success",
          confirmButtonText: "OK",
          showCancelButton: false,
        }).then((result) => {
          if (result.isConfirmed) {
            resetForm({
              values: {
                extend_creatives: [],
              },
              touched: {},
            });
          }
        });
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      extend_creatives: [],
    },
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: createValidationSchema(campaignData),
  });

  const {
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    values,
    isSubmitting,
  } = formik;

  const handleButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched
    await formik.setTouched({
      extend_creatives: true,
    });

    // Validate and submit
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.submitForm();
    }
  };

  const handleExtendCreativesChange = (v: Id[]) => {
    setFieldValue("extend_creatives", v);
    setFieldTouched("extend_creatives", true);
  };

  return (
    <div className="mb-4">
      <BulkSelectionLayout
        onToggleBulkSelection={onToggleBulkSelection}
        collapsedHeading="Want to extend in bulk"
        expandedHeading="Creative"
      >
        <Paper className="pb-2" elevation={0} square>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: "grow" }}>
              <FormLabel className="title fw-b">Select Creative</FormLabel>
              <RpMuiSelect
                name="extend_creatives"
                placeholder="Select Creative"
                options={creativeOptions}
                value={values?.extend_creatives || []}
                onChange={handleExtendCreativesChange}
                helperText={errors?.extend_creatives}
                onBlur={() => setFieldTouched("extend_creatives", true)}
                size="small"
                className="p-0"
              />
              {touched?.extend_creatives && errors?.extend_creatives && (
                <FormHelperText error>
                  {errors?.extend_creatives}
                </FormHelperText>
              )}
            </Grid>

            <Grid
              item
              size={{ xs: 12, lg: "grow" }}
              sx={{ textAlign: "right" }}
            >
              <Button
                onClick={handleButtonClick}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{
                  mt: { xs: 1, lg: 4 },
                  px: { xs: "auto", lg: 1, xl: 2 },
                }}
              >
                Apply to selected locations
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="caption">
          <span className="fw-b">Pro tip:</span> If you want to make minor
          changes to individual locations, first make bulk selections and then
          edit individual locations.
        </Typography>
      </BulkSelectionLayout>
    </div>
  );
}

export default ExtendCreativeBulkSelection;
